// WHAT: Custom hook for managing notifications state and operations
// WHY:  Centralizes notification data fetching, creation, and read status management
// DOMAIN: Admin → Notifications
// DEPENDENCIES: Supabase client, Notification type
// RISKS:
//   - WARN: Error handling is minimal - errors are silently ignored
//   - NOTE: No error state exposed to components
//   - NOTE: No loading state for async operations

import type { Notification } from "../types";

import { useEffect, useState, useCallback } from "react";

import { getSupabaseClient } from "@/shared/services";

/**
 * WHAT:
 *   Custom hook that manages notifications state and provides operations for fetching,
 *   creating, and marking notifications as read.
 *
 * WHY:
 *   Encapsulates notification-related logic in a reusable hook, providing a clean API
 *   for components to interact with notification data.
 *
 * RETURNS:
 *   - notifications: Notification[] — Array of all notifications
 *   - fetchNotifications: () => Promise<void> — Function to refresh notifications
 *   - markAsRead: (id: string) => Promise<void> — Function to mark notification as read
 *   - addNotification: (title: string, message: string) => Promise<void> — Function to create notification
 *   - unreadCount: number — Count of unread notifications
 *
 * BEHAVIOR:
 *   - Automatically fetches notifications on mount
 *   - Orders notifications by created_at descending (newest first)
 *   - Updates local state after successful operations
 *   - Calculates unread count from notifications array
 *
 * RISKS:
 *   - WARN: Errors are silently ignored - add proper error handling and user feedback
 *   - NOTE: No loading state - components can't show loading indicators
 *   - NOTE: No error state - components can't display error messages
 *   - NOTE: Type assertions (as Notification[]) may fail if database schema changes
 *
 * @returns {object} Objekt mit notifications, fetchNotifications, markAsRead, addNotification und unreadCount
 *
 */
export const useNotifications = () => {
  // NOTE: State for all notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);

  /**
   * WHAT:
   *   Fetches all notifications from Supabase, ordered by creation date.
   *
   * WHY:
   *   Provides a way to refresh notification data from the database.
   *
   * BEHAVIOR:
   *   - Queries notifications table
   *   - Orders by created_at descending (newest first)
   *   - Updates state with fetched data
   *
   * RISKS:
   *   - WARN: Errors are silently ignored - add error handling
   *   - NOTE: Type assertion may fail if database schema doesn't match Notification type
   */
  const fetchNotifications = useCallback(async () => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      // NOTE: Error handling should be implemented - log error or show user feedback
    } else {
      setNotifications(data as Notification[]);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  /**
   * WHAT:
   *   Marks a notification as read in the database and updates local state.
   *
   * WHY:
   *   Provides a way to update notification read status and keep UI in sync.
   *
   * BEHAVIOR:
   *   - Updates notification in database with read: true
   *   - Updates local state with updated notification
   *   - Only updates if database operation succeeds
   *
   * RISKS:
   *   - WARN: Errors are silently ignored - add error handling
   *   - NOTE: Type assertion may fail if database response doesn't match Notification type
   *
   * @param id
   *
   */
  const markAsRead = async (id: string) => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", id)
      .select();

    if (error) {
      // NOTE: Error handling should be implemented - log error or show user feedback
    } else if (data.length > 0) {
      setNotifications((previous) =>
        previous.map((n) => (n.id === id ? (data[0] as Notification) : n)),
      );
    }
  };

  /**
   * WHAT:
   *   Creates a new notification in the database and adds it to local state.
   *
   * WHY:
   *   Provides a way to create notifications and immediately update the UI.
   *
   * BEHAVIOR:
   *   - Inserts new notification into database
   *   - Adds notification to beginning of local state array (newest first)
   *   - Only updates if database operation succeeds
   *
   * RISKS:
   *   - WARN: Errors are silently ignored - add error handling
   *   - NOTE: No validation of title/message length or content
   *   - NOTE: Type assertion may fail if database response doesn't match Notification type
   *
   * @param title
   *
   * @param message
   *
   */
  const addNotification = async (title: string, message: string) => {
    const supabase = await getSupabaseClient();
    const result = await supabase
      .from("notifications")
      .insert({ message, title })
      .select()
      .single();
    const { data, error } = result as {
      data: Notification | null;
      error: unknown;
    };

    if (error) {
      // NOTE: Error handling should be implemented - log error or show user feedback
    } else if (data) {
      setNotifications((previous) => [data, ...previous]);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  return {
    addNotification,
    fetchNotifications,
    markAsRead,
    notifications,
    unreadCount,
  };
};
